import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PersonOff as DeactivateIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  AdminPanelSettings as AdminIcon,
  MedicalServices as PractitionerIcon,
  Work as StaffIcon,
  Visibility as ReadOnlyIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  History as ActivityIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Badge as BadgeIcon,
  CalendarToday,
  AccessTime
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser, useUpdateUser, useUpdateUserStatus } from '../../hooks/useUsers';
import { formatDistanceToNow } from 'date-fns';
import DevOnly from '../../components/DevOnly';

// Default permissions structure for new users
const defaultPermissions = {
  dashboard: true,
  clientManagement: false,
  scheduleManagement: false,
  financialAccess: false,
  reportsAnalytics: false,
  userManagement: false,
  systemSettings: false,
  adminFunctions: false
};

// Default schedule structure
const defaultSchedule = {
  monday: { start: '09:00', end: '17:00', active: true },
  tuesday: { start: '09:00', end: '17:00', active: true },
  wednesday: { start: '09:00', end: '17:00', active: true },
  thursday: { start: '09:00', end: '17:00', active: true },
  friday: { start: '09:00', end: '17:00', active: true },
  saturday: { start: '', end: '', active: false },
  sunday: { start: '', end: '', active: false }
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`user-tabpanel-${index}`}
    aria-labelledby={`user-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const getRoleIcon = (role) => {
  switch (role) {
    case 'admin': return <AdminIcon />;
    case 'practitioner': return <PractitionerIcon />;
    case 'staff': return <StaffIcon />;
    case 'readonly': return <ReadOnlyIcon />;
    default: return <AdminIcon />;
  }
};

const getRoleColor = (role) => {
  switch (role) {
    case 'admin': return 'error';
    case 'practitioner': return 'primary';
    case 'staff': return 'secondary';
    case 'readonly': return 'default';
    default: return 'default';
  }
};

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [localPermissions, setLocalPermissions] = useState(defaultPermissions);
  const [localSchedule, setLocalSchedule] = useState(defaultSchedule);

  // Fetch user data
  const { data: user, isLoading, error, refetch } = useUser(userId);
  const { mutate: updateUser } = useUpdateUser();
  const { mutate: updateUserStatus } = useUpdateUserStatus();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePermissionChange = (permission) => {
    setLocalPermissions(prev => ({
      ...prev,
      [permission]: !prev[permission]
    }));
  };

  const handleScheduleChange = (day, field, value) => {
    setLocalSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const handleStatusToggle = () => {
    if (user) {
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      updateUserStatus({ userId: user.id, status: newStatus });
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load user: {error.message}
          <Button onClick={() => refetch()} size="small" sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  // Show not found state
  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          User not found
          <Button onClick={() => navigate('/users')} size="small" sx={{ ml: 2 }}>
            Back to Users
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/users')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          User Profile
        </Typography>
      </Box>

      {/* User Header Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: '2rem' }}>
              {user.full_name?.split(' ').map(n => n[0]).join('') || 
               user.username?.[0]?.toUpperCase() || '?'}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Box>
              <Typography variant="h4" gutterBottom>
                {user.full_name || user.username || 'Unknown User'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Chip
                  icon={getRoleIcon(user.role || 'staff')}
                  label={(user.role || 'staff').charAt(0).toUpperCase() + (user.role || 'staff').slice(1)}
                  color={getRoleColor(user.role || 'staff')}
                />
                {user.status && user.status.trim() && (
                  <Chip
                    icon={<StaffIcon />}
                    label={user.status}
                    color="info"
                    variant="outlined"
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  Last login: {user.last_login
                    ? formatDistanceToNow(new Date(user.last_login), { addSuffix: true })
                    : 'Never'}
                </Typography>
              </Box>
              <Typography variant="subtitle1" color="text.secondary">
                {user.job_title || 'No title set'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2">{user.username || user.email || 'No email'}</Typography>
                </Box>
                {user.user_id && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BadgeIcon fontSize="small" color="action" />
                    <Typography variant="body2">ID: {user.user_id}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <DevOnly>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setEditDialogOpen(true)}
                >
                  Edit Profile
                </Button>
              </DevOnly>
              <DevOnly>
                <Button
                  variant="outlined"
                  color={user.status === 'active' ? 'error' : 'success'}
                  startIcon={user.status === 'active' ? <DeactivateIcon /> : <ActiveIcon />}
                  onClick={handleStatusToggle}
                >
                  {user.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
              </DevOnly>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Profile" icon={<BadgeIcon />} iconPosition="start" />
          <Tab label="Permissions" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="Schedule" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="Activity" icon={<ActivityIcon />} iconPosition="start" />
        </Tabs>

        {/* Profile Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Personal Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Pabau User ID" 
                        secondary={user.user_id || 'Not set'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Username" 
                        secondary={user.username || 'Not set'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Full Name" 
                        secondary={user.full_name || 'Not set'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Job Title" 
                        secondary={user.job_title || 'Not set'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Created Date" 
                        secondary={user.created_date 
                          ? new Date(user.created_date).toLocaleDateString() 
                          : 'Not available'} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Activity Statistics
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Total Appointments" 
                        secondary={user.total_appointments || 0} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Recent Appointments (30 days)" 
                        secondary={user.recent_appointments || 0} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Assigned Services"
                        secondary={user.assigned_services || 0}
                      />
                    </ListItem>
                    {user.status && user.status.trim() && (
                      <ListItem>
                        <ListItemText
                          primary="Status"
                          secondary={user.status}
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            {user.bio && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Bio & Notes
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {user.bio}
                    </Typography>
                    <Button variant="outlined" startIcon={<EditIcon />}>
                      Edit Bio
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Permissions Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Role Assignment
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Current Role</InputLabel>
                    <Select value={user.role || 'staff'} label="Current Role">
                      <MenuItem value="admin">Administrator</MenuItem>
                      <MenuItem value="practitioner">Practitioner</MenuItem>
                      <MenuItem value="staff">Staff</MenuItem>
                      <MenuItem value="readonly">Read Only</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="contained">Update Role</Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Feature Permissions
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Permission management is not yet implemented for Pabau users. 
                    Permissions are managed through the Pabau system.
                  </Alert>
                  <List>
                    {Object.entries(localPermissions).map(([permission, enabled]) => (
                      <ListItem key={permission}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={enabled}
                              onChange={() => handlePermissionChange(permission)}
                              disabled
                            />
                          }
                          label={permission.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Button variant="contained" sx={{ mt: 2 }} disabled>
                    Save Permissions
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Schedule Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Working Hours
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Schedule management is not yet implemented for Pabau users. 
                    Working hours are managed through the Pabau system.
                  </Alert>
                  <Grid container spacing={2}>
                    {Object.entries(localSchedule).map(([day, schedule]) => (
                      <Grid item xs={12} key={day}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                          <Typography sx={{ width: 100, textTransform: 'capitalize' }}>
                            {day}
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={schedule.active}
                                onChange={(e) => handleScheduleChange(day, 'active', e.target.checked)}
                                disabled
                              />
                            }
                            label=""
                          />
                          {schedule.active && (
                            <>
                              <TextField
                                type="time"
                                size="small"
                                value={schedule.start}
                                onChange={(e) => handleScheduleChange(day, 'start', e.target.value)}
                                sx={{ width: 120 }}
                                disabled
                              />
                              <Typography>to</Typography>
                              <TextField
                                type="time"
                                size="small"
                                value={schedule.end}
                                onChange={(e) => handleScheduleChange(day, 'end', e.target.value)}
                                sx={{ width: 120 }}
                                disabled
                              />
                            </>
                          )}
                          {!schedule.active && (
                            <Typography color="text.secondary">Closed</Typography>
                          )}
                        </Box>
                        <Divider />
                      </Grid>
                    ))}
                  </Grid>
                  <Button variant="contained" sx={{ mt: 2 }} disabled>
                    Update Schedule
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Service Assignments
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Service assignments are managed through Pabau.
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    This user's service assignments and availability are configured in the Pabau system.
                  </Typography>
                  <Button variant="outlined" fullWidth sx={{ mt: 2 }} disabled>
                    Manage Services
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Activity Tab */}
        <TabPanel value={activeTab} index={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Activity & Statistics
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Detailed activity tracking is not yet implemented for Pabau users. 
                Below are the available statistics from the sync data.
              </Alert>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <CalendarToday color="primary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" color="primary">
                        {user.total_appointments || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Appointments
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <AccessTime color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" color="secondary">
                        {user.recent_appointments || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Recent Appointments (30 days)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {user.status && user.status.trim() && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        {user.status.toLowerCase() === 'active' && (
                          <ActiveIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                        )}
                        {user.status.toLowerCase() === 'pending' && (
                          <AccessTime color="warning" sx={{ fontSize: 40, mb: 1 }} />
                        )}
                        {user.status.toLowerCase() === 'inactive' && (
                          <InactiveIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
                        )}
                        <Typography
                          variant="h4"
                          color={
                            user.status.toLowerCase() === 'active' ? 'success.main' :
                            user.status.toLowerCase() === 'pending' ? 'warning.main' :
                            user.status.toLowerCase() === 'inactive' ? 'error.main' :
                            'text.primary'
                          }
                        >
                          {user.status}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Current Status
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Last Login Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.last_login 
                    ? `Last logged in ${formatDistanceToNow(new Date(user.last_login), { addSuffix: true })}`
                    : 'No login information available'
                  }
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                defaultValue={user.full_name || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                defaultValue={user.username || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Job Title"
                defaultValue={user.job_title || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                defaultValue={user.email || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={user.role || 'staff'}
                  label="Role"
                >
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="practitioner">Practitioner</MenuItem>
                  <MenuItem value="staff">Staff</MenuItem>
                  <MenuItem value="readonly">Read Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={user.status || 'active'}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {user.bio && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Bio"
                  defaultValue={user.bio || ''}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setEditDialogOpen(false)}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetail;